import { Formik, useFormikContext } from 'formik';
import { DepartmentsSelect } from 'views/Dashboard/ImplementData/components/Filter/DepartmentSelect';
import {
  usePlatformSelect,
  useStoreInfoSelect,
  useStoreTypeSelect,
} from 'views/Dashboard/ImplementData/components/Filter/Provider';
import { useEffect, useMemo } from 'react';
import type { SourceOptions, TagSource, DateTarget, DateTargetOptions, FilterValue } from '../type';
import { Select, SelectProps, CascaderProps, Input, Radio, Cascader } from 'antd';
import { DatePicker } from 'components/DatePicker';
import { cx } from '@emotion/css';
import { Overview } from './Overview';
import useSmartField from 'hooks/useSmartField';
import dayjs, { Dayjs } from 'dayjs';
import { useCreation, useLatest, useRequest } from 'ahooks';
import { getAllTags } from '../service';
import { flatten, map } from 'lodash-es';
import { cutTag, groupTagByName, Option, useTagTransName } from '../util';
import LastOperatorSelect from './LastOperatorSelect';
import { useTranslate } from '@tolgee/react';

const getDefaultDateRange = (): [Dayjs, Dayjs] => {
  return [dayjs().subtract(7, 'day'), dayjs()];
};

const getDefaultMonthRange = (): [Dayjs, Dayjs] => {
  return [dayjs().subtract(1, 'month'), dayjs()];
};

export const TagOverView = () => {
  const { t } = useTranslate();
  const dateRangeRef = useCreation(() => getDefaultDateRange(), []);
  const monthRangeRef = useCreation(() => getDefaultMonthRange(), []);

  return (
    <Formik<FilterValue>
      initialValues={{
        platform: 'ALL',
        storeInfo: {},
        keywordType: 'spuClassName',
        keyword: '',
        dateTarget: 'TAG_UPDATE',
        pickerType: 'date',
        dateRange: dateRangeRef,
        monthRange: monthRangeRef,
        source: [],
        tagId: [],
      }}
      onSubmit={() => {}}
    >
      {({ values }) => {
        return (
          <div className="mt-[16px] rounded-[4px] h-full" id="beisi-tag-container">
            <div className="bg-[var(--fett-bg-panel)]">
              <div className="text-[16px] leading-[22px] px-[24px] py-[17px] font-medium border-solid border-0 border-b border-[rgba(0,0,0,.05)]">
                {t('bs_46')}
              </div>
              <Filter />
            </div>

            <Overview values={values} />
          </div>
        );
      }}
    </Formik>
  );
};

const Filter = () => {
  const { values, setFieldValue } = useFormikContext<FilterValue>();
  const {
    departments,
    platform,
    storeType,
    storeInfo,
    lastOperatorId,
    isReviewUpdated,
    dateTarget,
    source,
    tagId,
  } = values;
  const { regionInfo, platformOptions, platformSelect } = usePlatformSelect({
    departments,
    scene: 'ORDER_STAT',
    value: platform,
    showPlatformAll: true,
    className: '!min-w-[142px]',
    onChange: (value) => {
      setFieldValue('platform', value);
      setFieldValue('storeInfo', {});
    },
  });

  const { storeTypeSelect } = useStoreTypeSelect({
    value: storeType,
    onChange: (storeType) => {
      setFieldValue('storeType', storeType);
      setFieldValue('storeInfo', {});
    },
    className: '!min-w-[142px]',
  });

  const { storeInfoSelect } = useStoreInfoSelect({
    regionInfo,
    platform,
    departments,
    platformOptions,
    storeType,
    storeInfo,
    onChange: (storeInfo) => setFieldValue('storeInfo', storeInfo),
    scene: 'ORDER_STAT',
    className: '!min-w-[170px]',
  });

  useEffect(() => {
    if (platform) return;
    setFieldValue('platform', 'ALL');
  }, [platformOptions, platform, setFieldValue]);

  return (
    <div className="flex flex-wrap gap-[16px] items-center px-[24px] py-[16px]">
      <DepartmentsSelect
        dashboardType="order"
        style={{ minWidth: '142px' }}
        value={departments}
        onChange={(value) => {
          setFieldValue('departments', value);
          setFieldValue('platform', undefined);
          setFieldValue('storeInfo', {});
        }}
      />
      {platformSelect}
      {storeTypeSelect}
      {storeInfoSelect}
      <SourceSelect
        value={source}
        onChange={(value) => {
          setFieldValue('source', value);
        }}
      />
      <TagSelect
        value={tagId}
        onChange={(value: any) => {
          setFieldValue('tagId', value);
        }}
        onIdMapChange={(map) => {
          setFieldValue('tagIdMap', map);
        }}
      />
      <LastOperatorSelect
        className="min-w-[180px]"
        value={lastOperatorId}
        onChange={(value) => {
          setFieldValue('lastOperatorId', value);
        }}
      />
      <KeyWordSearch />
      <ReviewUpdateSelect
        value={isReviewUpdated}
        onChange={(value) => {
          setFieldValue('isReviewUpdated', value);
        }}
      />
      <DateTargetSelect
        className="!ml-auto"
        value={dateTarget}
        onChange={(value) => {
          setFieldValue('dateTarget', value);
        }}
      />
      <DateSelect />
    </div>
  );
};

interface SourceSelectProps extends SelectProps<TagSource[]> {}
const SourceSelect = (props: SourceSelectProps) => {
  const { t } = useTranslate();
  const options = useMemo<SourceOptions>(
    () => [
      {
        value: 'CONVERSATION',
        label: t('header.会话'),
      },
      {
        value: 'ORDER',
        label: t('ViewsChat.弃单'),
      },
      {
        value: 'REVIEW',
        label: t('review.评价'),
      },
      {
        value: 'TODO',
        label: t('todo.待办'),
      },
    ],
    [t],
  );
  return (
    <Select
      className="min-w-[165px]"
      mode="multiple"
      dropdownMatchSelectWidth={false}
      placeholder={t('bs_47')}
      options={options}
      {...props}
    ></Select>
  );
};

interface DateTargetSelectProps extends SelectProps<DateTarget> {}
export const DateTargetSelect = (props: DateTargetSelectProps) => {
  const { className, ...restProps } = props;
  const { t } = useTranslate();
  const options = useMemo<DateTargetOptions>(
    () => [
      {
        value: 'TAG_UPDATE',
        label: t('bs_48'),
      },
      {
        value: 'ORDER_CREATE',
        label: t('bs_49'),
      },
      {
        value: 'ORDER_PAY',
        label: t('bs_50'),
      },
    ],
    [t],
  );
  return (
    <Select
      className={cx(className, 'min-w-[165px]')}
      dropdownMatchSelectWidth={false}
      options={options}
      {...restProps}
    />
  );
};

interface TagSelectProps extends CascaderProps<Option> {
  onIdMapChange?: (map: Record<string, number[]>) => void;
}
const TagSelect = (props: TagSelectProps) => {
  const { onIdMapChange } = props;
  const onIdMapChangeRef = useLatest(onIdMapChange);
  const { data, loading } = useRequest(() => getAllTags());
  const { t } = useTranslate();
  const filterPath = (inputValue: string, path: any[]) => {
    return path.some(
      (option) => (option.name as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1,
    );
  };
  const getTagName = useTagTransName();
  const [options, idMap] = useMemo(() => {
    const tags = getTagName(flatten(map(data, (o) => o.tags)));
    let idMap: Record<string, number[]> = {};
    const [group1, idMap1] = groupTagByName(tags);
    idMap = { ...idMap, ...idMap1 };
    const options = map(group1, (each) => {
      const [d, p] = groupTagByName(each.subTags as any);
      idMap = { ...idMap, ...p };
      return {
        ...each,
        subTags: d,
      };
    });
    return [cutTag(options as any), idMap];
  }, [data, getTagName]);

  useEffect(() => {
    onIdMapChangeRef.current?.(idMap);
  }, [idMap, onIdMapChangeRef]);

  return (
    <div className="flex-grow">
      <Cascader
        className="!w-full min-w-[165px]"
        options={options as any}
        multiple
        placeholder={t('bs_38')}
        showSearch={{ filter: filterPath }}
        fieldNames={{ label: 'name', value: 'id', children: 'subTags' }}
        loading={loading}
        {...props}
      />
    </div>
  );
};

const KeyWordSearch = () => {
  const { values, setFieldValue } = useFormikContext<FilterValue>();
  const { keywordType } = values;
  const { t } = useTranslate();
  const options = useMemo(() => {
    return [
      {
        value: 'spuClassName',
        label: t('bs_53'),
      },
      {
        value: 'physicalProductName',
        label: t('bs_9'),
      },
      {
        value: 'sellerSku',
        label: t('bs_52'),
      },
      {
        value: 'spuId',
        label: t('sider.商品ID'),
      },
    ];
  }, [t]);
  return (
    <div className="w-[290px]">
      <Input.Search
        addonBefore={
          <Select
            value={keywordType}
            options={options}
            onChange={(value) => setFieldValue('keywordType', value)}
          />
        }
        onSearch={(value) => {
          setFieldValue('keyword', value);
        }}
      />
    </div>
  );
};

interface ReviewUpdateSelectProps extends SelectProps {}
const ReviewUpdateSelect = (props: ReviewUpdateSelectProps) => {
  const { t } = useTranslate();
  return (
    <Select
      allowClear
      className="min-w-[165px]"
      options={[
        { value: true as unknown as string, label: t('bs_55') },
        { value: false as unknown as string, label: t('bs_54') },
      ]}
      placeholder={t('bs_79')}
      dropdownMatchSelectWidth={false}
      {...props}
    />
  );
};

export const DateSelect = () => {
  const { field, helpers } = useSmartField<FilterValue['pickerType']>('pickerType');
  const { field: dateField, helpers: dateHelpers } =
    useSmartField<FilterValue['dateRange']>('dateRange');
  const { field: monthField, helpers: monthHelpers } =
    useSmartField<FilterValue['monthRange']>('monthRange');
  const { t } = useTranslate();
  return (
    <Input.Group compact className="flex !w-fit">
      <Radio.Group value={field.value} onChange={(e) => helpers.setValue(e.target.value)}>
        <Radio.Button value="date">{t('bs_81')}</Radio.Button>
        <Radio.Button value="month">{t('bs_82')}</Radio.Button>
      </Radio.Group>
      <DatePicker.RangePicker
        picker={field.value}
        allowClear={false}
        value={field.value === 'date' ? dateField.value : monthField.value}
        onChange={(value) => {
          if (field.value === 'date') {
            dateHelpers.setValue(value as [Dayjs, Dayjs]);
          } else {
            monthHelpers.setValue(value as [Dayjs, Dayjs]);
          }
        }}
      />
    </Input.Group>
  );
};
